import React, { useState, useEffect } from 'react';
import { FaTimes, FaArrowRight } from 'react-icons/fa';
import { IoMdArrowForward } from 'react-icons/io';
import filterMovementImg from './image.png'; // Import the image

const FeatureTour = ({ onClose }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [highlightPosition, setHighlightPosition] = useState({ top: 0, left: 0, width: 0, height: 0 });
  const [showTooltip, setShowTooltip] = useState(true); // Control tooltip visibility for animation

  const steps = [
    {
      id: 'chart-view',
      title: 'New Feature!',
      description: 'Experience better data visualization with our new chart view.',
      selector: 'button[aria-label="chart-view-button"]',
      position: 'bottom-left',
      badge: 'NEW'
    },
    {
      id: 'filter-icon',
      title: "We Moved 'All Filters' here!",
      description: 'Refine Search and Show Deals By have been combined to a single filter for easier access.',
      selector: 'button[aria-label="filter-button"]',
      position: 'bottom-left',
      badge: 'MOVED'
    }
  ];

  useEffect(() => {
    updateHighlightPosition();
    window.addEventListener('resize', updateHighlightPosition);
    
    // Animation: Show tooltip after 2 seconds on step 2 (filter)
    if (currentStep === 1) {
      setShowTooltip(false); // Hide tooltip initially
      const timer = setTimeout(() => {
        setShowTooltip(true); // Show tooltip after 1 second
      }, 1000);
      
      return () => {
        clearTimeout(timer);
        window.removeEventListener('resize', updateHighlightPosition);
      };
    } else {
      setShowTooltip(true); // Show immediately for other steps
    }
    
    // Add click listeners based on current step
    const filterButton = document.querySelector('button[aria-label="filter-button"]');
    const chartButton = document.querySelector('button[aria-label="chart-view-button"]');
    
    if (currentStep === 0 && chartButton) {
      chartButton.addEventListener('click', handleNext);
    }
    
    if (currentStep === 1 && filterButton) {
      filterButton.addEventListener('click', handleClose);
    }
    
    return () => {
      window.removeEventListener('resize', updateHighlightPosition);
      if (filterButton) {
        filterButton.removeEventListener('click', handleClose);
      }
      if (chartButton) {
        chartButton.removeEventListener('click', handleNext);
      }
    };
  }, [currentStep]);

  const updateHighlightPosition = () => {
    const currentStepData = steps[currentStep];
    const element = document.querySelector(currentStepData.selector);
    
    if (element) {
      const rect = element.getBoundingClientRect();
      setHighlightPosition({
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height
      });
    }
  };

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleClose();
    }
  };

  const handleSkip = () => {
    handleClose();
  };

  const handleClose = () => {
    onClose();
  };

  const getTooltipPosition = () => {
    const step = steps[currentStep];
    const padding = 20;
    const tooltipWidth = 350; // Approximate width of tooltip
    
    let style = {
      position: 'fixed',
      zIndex: 10003, // Higher than overlay (10000) to appear on top
    };

    // Calculate if tooltip would go off-screen on the right
    const wouldOverflow = (highlightPosition.left + tooltipWidth) > window.innerWidth;
    
    if (wouldOverflow) {
      // Position from the right edge to prevent cutoff
      style.top = `${highlightPosition.top + highlightPosition.height + padding}px`;
      style.right = `${window.innerWidth - (highlightPosition.left + highlightPosition.width)}px`;
    } else {
      // Position normally from the left
      style.top = `${highlightPosition.top + highlightPosition.height + padding}px`;
      style.left = `${highlightPosition.left}px`;
    }

    return style;
  };

  return (
    <>
      {/* Overlay - Different style for filter step */}
      {currentStep === 1 ? (
        // Step 2: Show image in center WITH filter icon cutout
        <>
          <div 
            className="fixed inset-0 z-[10000]"
            onClick={handleSkip}
            style={{
              background: 'rgba(0, 0, 0, 0.6)',
              clipPath: `polygon(
                0% 0%, 
                0% 100%, 
                ${highlightPosition.left - 8}px 100%, 
                ${highlightPosition.left - 8}px ${highlightPosition.top - 8}px, 
                ${highlightPosition.left + highlightPosition.width + 8}px ${highlightPosition.top - 8}px, 
                ${highlightPosition.left + highlightPosition.width + 8}px ${highlightPosition.top + highlightPosition.height + 8}px, 
                ${highlightPosition.left - 8}px ${highlightPosition.top + highlightPosition.height + 8}px, 
                ${highlightPosition.left - 8}px 100%, 
                100% 100%, 
                100% 0%
              )`
            }}
          >
            {/* Center Image showing filter movement */}
            <div className="absolute top-[57%] left-[14%] transform -translate-x-1/2 -translate-y-1/2 pointer-events-none">
              <div className="relative">
                {/* Number 1 Badge - Top Right of Image */}
                <div className="absolute -top-3 -right-3 z-10 bg-[#BC8B20] text-white w-10 h-10 rounded-full flex items-center justify-center text-xl font-bold shadow-lg">
                  1
                </div>
                <img 
                  src={filterMovementImg} 
                  alt="Filter moved from sidebar to icon" 
                  className="max-w-4xl w-auto h-auto rounded-lg shadow-2xl"
                  style={{ maxHeight: '80vh' }}
                />
              </div>
            </div>
          </div>
        </>
      ) : (
        // Step 1: Regular cutout overlay for chart view
        <div 
          className="fixed inset-0 z-[10000]"
          onClick={handleSkip}
          style={{
            background: 'rgba(0, 0, 0, 0.6)',
            clipPath: `polygon(
              0% 0%, 
              0% 100%, 
              ${highlightPosition.left - 8}px 100%, 
              ${highlightPosition.left - 8}px ${highlightPosition.top - 2}px, 
              ${highlightPosition.left + highlightPosition.width + 8}px ${highlightPosition.top - 2}px, 
              ${highlightPosition.left + highlightPosition.width + 8}px ${highlightPosition.top + highlightPosition.height}px, 
              ${highlightPosition.left - 8}px ${highlightPosition.top + highlightPosition.height }px, 
              ${highlightPosition.left - 8}px 100%, 
              100% 100%, 
              100% 0%
            )`
          }}
        />
      )}

      {/* Tooltip */}
      {showTooltip && (
        <div 
          className={`bg-white rounded-lg shadow-2xl p-4 max-w-sm relative ${
            currentStep === 1 ? 'animate-fadeIn' : ''
          }`}
          style={getTooltipPosition()}
        >
          {/* Number 2 Badge - Top Left of Tooltip (only for step 2) */}
          {currentStep === 1 && (
            <div className="absolute -top-3 -left-3 bg-[#BC8B20] text-white w-10 h-10 rounded-full flex items-center justify-center text-xl font-bold shadow-lg">
              2
            </div>
          )}
        
        {/* Content */}
        <div>
          {/* <div className="flex items-center gap-2 mb-3">
            <span className="bg-[#BC8B20] text-white text-xs font-semibold px-2 py-1 rounded">
              {steps[currentStep].badge}
            </span>
            <span className="text-sm text-gray-500">
              {currentStep + 1} of {steps.length}
            </span>
          </div> */}
          
          <h3 className="text-xl font-bold text-gray-900 mb-2">
            {steps[currentStep].title}
          </h3>
          
          <p className="text-gray-600 mb-3 leading-relaxed">
            {steps[currentStep].description}
          </p>

          {/* Progress dots and Buttons */}
          <div className="flex items-center justify-between">
            <div className="flex gap-2">
              {steps.map((_, index) => (
                <div
                  key={index}
                  className={`h-2 rounded-full transition-all ${
                    index === currentStep
                      ? 'w-8 bg-[#BC8B20]'
                      : 'w-2 bg-gray-300'
                  }`}
                />
              ))}
            </div>

            <div className="flex gap-3">
              {currentStep < steps.length - 1 ? (
                <>
                  {/* <button
                    onClick={handleSkip}
                    className="text-gray-500 hover:text-gray-700 text-sm font-medium transition-colors"
                  >
                    Skip
                  </button> */}
                  <button
                    onClick={handleNext}
                    className="flex items-center gap-2 bg-[#BC8B20] hover:bg-[#a67a1c] text-white px-1 py-1 rounded-lg font-medium transition-colors"
                  >
                    Next
                    <IoMdArrowForward />
                  </button>
                </>
              ) : (
                <button
                  onClick={handleClose}
                  className="bg-[#BC8B20] hover:bg-[#a67a1c] text-white px-1 py-1 rounded-lg font-medium transition-colors"
                >
                  Got it!
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
      )}

      {/* Add CSS animation */}
      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .animate-fadeIn {
          animation: fadeIn 0.5s ease-out forwards;
        }
      `}</style>
    </>
  );
};

export default FeatureTour;
